// src/pages/Cart/CartPage.jsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Toast from "../../components/Toast";
import { useCartContext } from "../../components/CartContext";

function GlassTile({ className = "", children }) {
  return (
    <div
      className={
        "bg-white/40 backdrop-blur-md border border-white/25 rounded-2xl shadow-md " +
        className
      }
      style={{ WebkitBackdropFilter: "blur(12px) saturate(140%)" }}
    >
      {children}
    </div>
  );
}

export default function CartPage() {
  const navigate = useNavigate();

  // 👇 Get cart + helpers from shared hook
  const {
    cart,
    updateQty,
    isLoggedIn,
    subtotal,
    shipping,
    taxes,
    total,
  } = useCartContext();

  const [toast, setToast] = useState("");

  const handleCheckout = () => {
    if (!isLoggedIn) {
      setToast("Please login to proceed to checkout");
      return navigate("/authlogin");
    }
    navigate("/CheckoutPageNew");
  };

  const handleRemove = (variantId, name) => {
    updateQty(variantId, 0);
    setToast(`${name} removed from cart`);
  };

  return (
    <div className="min-h-screen overflow-y-auto">
      <main className="max-w-7xl mx-auto px-6 pt-28 pb-20">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Your Cart</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Items */}
          <GlassTile className="flex-1 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Items Added
            </h2>

            {cart.length === 0 ? (
              <p className="text-gray-600">Your cart is empty.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {cart.map((item) => (
                  <GlassTile key={item.variantId} className="p-4">
                    <div className="flex flex-col items-center">
                      <img
                        src={item.imageUrl}
                        alt={item.productName}
                        className="w-32 h-32 object-contain"
                      />

                      <p className="font-semibold mt-3 text-gray-800">
                        {item.productName}
                      </p>

                      <p className="text-sm text-gray-600">
                        {item.color} / {item.size}
                      </p>

                      <p className="text-xs text-gray-500">
                        SKU: {item.sku}
                      </p>


                      <p className="text-gray-600">₹{item.price}</p>

                      {/* Quantity Controls - Style 1 */}
                      <div className="mt-3 flex items-center">
                        <div
                          className="
                            flex items-center 
                            bg-white/60 backdrop-blur-md 
                            border border-white/30 
                            rounded-xl shadow 
                            overflow-hidden
                          "
                        >
                          {/* Minus */}
                          <button
                            onClick={() =>
                              updateQty(item.variantId, item.quantity - 1)
                            }
                            className="
                              px-4 py-2 
                              text-gray-700 font-bold 
                              hover:bg-gray-100 
                              transition
                            "
                          >
                            -
                          </button>

                          {/* Qty */}
                          <span
                            className="
                              px-4 py-2 
                              font-semibold 
                              text-gray-800 
                              select-none
                            "
                          >
                            {item.quantity}
                          </span>

                          {/* Plus */}
                          <button
                            onClick={() =>
                              updateQty(item.variantId, item.quantity + 1)
                            }
                            className="
                              px-4 py-2 
                              font-bold text-white 
                              bg-blue-600 hover:bg-blue-700 
                              transition
                            "
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() =>
                          handleRemove(item.variantId, item.productName)
                        }
                        className="
                          mt-4 px-4 py-2 
                          rounded-xl text-sm text-white 
                          bg-gradient-to-r from-red-500 to-pink-500 
                          shadow hover:opacity-90 transition
                        "
                      >
                        Remove
                      </button>
                    </div>
                  </GlassTile>
                ))}
              </div>
            )}
          </GlassTile>

          {/* Summary */}
          <GlassTile className="w-full lg:w-96 p-6 h-fit">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Order Summary
            </h2>

            <ul className="space-y-2 text-gray-700">
              <li className="flex justify-between">
                <span>Subtotal</span>
                <span>₹{subtotal}</span>
              </li>
              <li className="flex justify-between">
                <span>Shipping</span>
                <span>{shipping === 0 ? "Free" : `₹${shipping}`}</span>
              </li>
              <li className="flex justify-between">
                <span>Taxes </span>
                <span>{taxes}</span>
              </li>
              <li className="flex justify-between font-bold text-xl mt-3">
                <span>Total</span>
                <span>₹{total}</span>
              </li>
            </ul>

            <button
              onClick={handleCheckout}
              className={`
                w-full mt-6 py-3 rounded-xl shadow transition
                ${isLoggedIn
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-400 text-white cursor-not-allowed"
                }
              `}
            >
              {isLoggedIn ? "Proceed to Checkout" : "Login to Checkout"}
            </button>
          </GlassTile>
        </div>
      </main>

      {/* Toast */}
      {toast && <Toast message={toast} onClose={() => setToast("")} />}
    </div>
  );
}
